/**
 * Eid al-Adha gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidAlAdhaGiftTagsPack: ToilePack = {
    slug: 'eid-al-adha-gift-tags',
    category: 'season',
    name: 'Eid al-Adha gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'eidi', label: 'Eidi tags', subject: 'a sheet of six tags reading "Your Eidi" with a small crescent and star, a "To:" line beneath, sized to clip onto a money envelope'},
        {key: 'sweets', label: 'Sweet box tags', subject: 'a sheet of round tags reading "Eid Mubarak" framed by a ring of small lanterns, a punched hole at the top'},
        {key: 'family', label: 'From our family', subject: 'a sheet of arch-shaped tags with "Eid al-Adha Mubarak" at the top and "From the family of" above a blank line'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'tags in white and dark ink as if printed on kraft paper, hand-stamped look, simple line motifs', palette: 'kraft brown with white and deep green ink', framing: 'Flat on the sheet, evenly spaced'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'emerald, gold, deep blue and cream, four colors', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'lineart', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills, simple hand lettering', palette: 'black ink only', framing: 'Drawn on plain white paper with a clean margin'},
    ],
    directions: ['Money envelope', 'Gold foil', 'Blue palette'],
    directionHint: 'try: Money envelope',
    seo: {
        title: 'Eid al-Adha Gift Tags: Eidi, Sweets, Family',
        metaDescription: 'Printable Eid al-Adha gift tags for Eidi envelopes, sweet boxes and family gifts, in kraft, flat color and line art.',
        h1: 'Eid al-Adha gift tags: Eidi, sweet boxes, from the family',
        tagline: 'Nine sheets of cut-out tags with the greeting printed and a line for a name, in three styles.',
        intro: 'Printable tags for the gifts of Eid al-Adha: the Eidi money envelopes given to children, sweet boxes taken to neighbours, and family gifts. Eidi tags read Your Eidi with a crescent and star and clip onto an envelope. Sweet box tags carry Eid Mubarak inside a ring of lanterns, and family tags print a From the family of line. Kraft paper, flat color or line art. Type a different greeting, and the same tag layout carries it.',
        useCases: [
            'a tag on an Eidi money envelope',
            'a tag on a box of sweets for a neighbour',
            'a tag on a family gift',
            'a label for a school Eid celebration',
        ],
        faq: [
            {q: 'Is there a line for a name?', a: 'Yes. The Eidi tags have a To line, and the family tags have a From the family of line.'},
            {q: 'What is Eidi?', a: 'Eidi is the gift of money given to children at Eid.'},
            {q: 'Can the greeting be changed?', a: 'Yes. Type a different greeting, and the same tag layout carries it.'},
        ],
    },
}
